import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData, getseriesType} from '../../../services/management/accommodationSetup/accommodationSetupServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';


const AccommodationSetup = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({ id: '', series: '', sub_domain: '', venue: '', hotel_name: '', conf_shortname: ''});
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [subdomainType, setsubdomainType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  
  // Fetch data list
  const fetchData = useCallback(async () => {
    try {
      const data = await getData();
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true); 
      await fetchData();
      // Optional 2 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false); 
    };
  
    fetchDataAsync();
  }, [fetchData]);

  useEffect(() => {
    const fetchseriesType = async () => {
      try {
        const data = await getseriesType(); // Fetch data from the service
         const sortedData = [...data].sort((a, b) =>
        a.sub_domain.localeCompare(b.sub_domain)
      );
      setsubdomainType(sortedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchseriesType();
  }, []);

  const handleChange = (e) => {
  const { name, value } = e.target;

  // Update the field as usual
  setForm((prev) => ({ ...prev, [name]: value }));

  // If user changes subdomain, auto-fill series & venue
  if (name === 'sub_domain') {
    const selected = subdomainType.find((item) => item.sub_domain === value);
    if (selected) {
      setForm((prev) => ({
        ...prev,
        sub_domain: value,
        series: selected.series || '',
        venue: selected.venue || '',
      }));
    } else {
      // Reset if no match found
      setForm((prev) => ({
        ...prev,
        sub_domain: value,
        series: '',
        venue: '',
      }));
    }
  }

  // Clear any existing field errors
  setErrors((prev) => ({ ...prev, [name]: '' }));
};


  const validateForm = () => {
    const newErrors = {};
    // Check if code or name fields are empty or just whitespace
    if (!form.series || !form.series.trim()) {newErrors.series = 'Series is required';}
    if (!form.sub_domain || !form.sub_domain.trim()) {newErrors.sub_domain = 'Subdomain is required';}
    if (!form.venue || !form.venue.trim()) {newErrors.venue = 'Venue is required';}
    if (!form.hotel_name || !form.hotel_name.trim()) {newErrors.venue = 'hotel_name is required';}
    if (!form.conf_shortname || !form.conf_shortname.trim()) {newErrors.venue = 'conf_shortname is required';}
    // Set the error state if there are validation errors
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;  // Return true if no errors
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (modalType === 'add') {
        await addData(form);
      } else if (modalType === 'edit') {
        await updateData(form);
      }
      fetchData();
      setShowModal(false);
    // ✅ Show success alert with auto-close
        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `Accommodation setup ${modalType === 'add' ? 'added' : 'updated'} successfully!`,
            timer: 2000, // 2 seconds
            showConfirmButton: false,
        });
        } catch (error) {
        console.error('Error during form submission:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Something went wrong while submitting the form!',
        });
        }
  };

  const handleDelete = async () => {
    if (selectedData) {
      // Optimistically remove the data from the list
      setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
      // Proceed with the actual delete request
      await deleteData(selectedData);
      setShowDeleteConfirmModal(false);
    }
  };

  const openModal = (type, data = { id: '', series: '', sub_domain: '', venue: '', hotel_name: '', conf_shortname: '' }) => {
    setErrors({}); // ✅ Clear previous errors
    setModalType(type);
    setForm(data);
    setShowModal(true);
  };

  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Memoized filtered data based on search query
  const filteredData = useMemo(() => {
    return dataList.filter(
      (data) =>
        data.hotel_name.toLowerCase().includes(searchText.toLowerCase()) ||
        data.venue.toLowerCase().includes(searchText.toLowerCase()) ||
        data.conf_shortname.toLowerCase().includes(searchText.toLowerCase()) ||
        data.sub_domain.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); // Descending by updatedAt
  }, [dataList, searchText]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '80px',
    },
    {
    name: 'Series',
    selector: (row) => {
        if (!row.series) return '';
        const date = new Date(row.series + "-01"); // append day to make it valid
        return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    sortable: true,
    },
    {
    name: 'Subdomain',
    selector: (row) => row.sub_domain,
    sortable: true,
    },
    {
    name: 'Venue',
    selector: (row) => row.venue,
    sortable: true,
    },
    {
    name: 'Hotel Name',
    selector: (row) => row.hotel_name,
    sortable: true,
    },
    {
    name: 'Shortname',
    selector: (row) => row.conf_shortname,
    sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)} > Edit </Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)} > Delete </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100"
            style={{ minHeight: "300px" }}
          >
            <Spinner/>
          </div>
                  ) :(
        <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
            <p className='fs-4'>Conference Management / Configurations / Accommodation Setup</p>
            </div>
            <div className="col-4 text-end">
              <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>
                Add
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            search
            highlightOnHover
            responsive
            striped
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
            onChangePage={handlePageChange}
            subHeader
            subHeaderComponent={
              <div>
                <input className="form-control" type="text" placeholder="Search" value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            }
          />

          {/* Modal for Add/Edit data */}
          <Modal show={showModal} backdrop="static" className='modal-lg' onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>    
                <div className="row">      
                <Form.Group className="col-md-4 mb-3">
                <Form.Label>
                    Subdomain Type <span className="text-danger">*</span>
                </Form.Label>

                {modalType === 'edit' ? (
                    // Show read-only text field when editing
                    <Form.Control type="text" name="sub_domain" value={form.sub_domain} readOnly disabled/>
                    ) : (
                    // Show dropdown only when adding
                    <Form.Control className="form-select mb-3" as="select" name="sub_domain" value={form.sub_domain} onChange={handleChange} isInvalid={!!errors.sub_domain}>
                    <option value="">Select</option>
                    {subdomainType
                    .filter((subdomain) => !dataList.some((d) => d.sub_domain === subdomain.sub_domain))
                    .map((subdomain) => (
                        <option key={subdomain.sub_domain} value={subdomain.sub_domain}>
                        {subdomain.sub_domain}
                    </option>
                    ))}
                    </Form.Control>
                )}

                <Form.Control.Feedback type="invalid">{errors.sub_domain}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                <Form.Label>Venue<span className="text-danger">*</span></Form.Label>
                <Form.Control type="text" name="venue" value={form.venue} onChange={handleChange} isInvalid={!!errors.venue} />
                <Form.Control.Feedback type="invalid">  {errors.venue} </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>Series<span className="text-danger">*</span></Form.Label>
                  <Form.Control type="month" name="series" value={form.series} onChange={handleChange} isInvalid={!!errors.series}/>
                  <Form.Control.Feedback type="invalid">  {errors.series} </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                <Form.Label>Hotel Name<span className="text-danger">*</span></Form.Label>
                <Form.Control type="text" name="hotel_name" value={form.hotel_name} onChange={handleChange} isInvalid={!!errors.hotel_name}/>
                <Form.Control.Feedback type="invalid">  {errors.hotel_name} </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                <Form.Label>Conf Shortname<span className="text-danger">*</span></Form.Label>
                <Form.Control type="text" name="conf_shortname" value={form.conf_shortname} onChange={handleChange} isInvalid={!!errors.conf_shortname}/>
                <Form.Control.Feedback type="invalid">  {errors.conf_shortname} </Form.Control.Feedback>
                </Form.Group>       
                </div>   
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}> Cancel </Button>
              <Button variant="primary" onClick={handleSubmit}> {modalType === 'add' ? 'Add' : 'Save'} </Button>
            </Modal.Footer>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this row?</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
        <Footer />
        </div>
        )}
      </div>
    </div>
  );
};

export default AccommodationSetup;